import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import {
    PAYMENT_CANCEL_SHORTCUT,
    PAYMENT_CONFIRM_SHORTCUT,
    PAYMENT_SCOPE,
    renderWithKeyShortcuts,
    ShortcutHint,
} from '@/components/shortcuts';
import { formatGel } from '../sellerMeta';
import type { PaymentFlowValue } from './usePaymentFlow';
import { usePaymentPhysicalKeys } from './usePaymentPhysicalKeys';
import { useCombinedLegKeys } from './useCombinedLegKeys';
import { PaymentScope } from './PaymentScope';
import { PaymentMethodSelect } from './components/PaymentMethodSelect';
import { CashSection } from './components/CashSection';
import { CombinedSection } from './components/CombinedSection';
import { TerminalSection } from './components/TerminalSection';

const renderCancelButton = renderWithKeyShortcuts(
    'button',
    PAYMENT_CANCEL_SHORTCUT.combination,
);
const renderConfirmButton = renderWithKeyShortcuts(
    'button',
    PAYMENT_CONFIRM_SHORTCUT.combination,
);

/**
 * The checkout payment modal shell. Render-only — every value comes from
 * `usePaymentFlow` (passed down from `CartPanel`). Shows the amount due (the
 * discounted total; a discounted sale adds the subtotal struck through and
 * the sum of its discounts beneath it, SEL-100), the
 * method picker, and the method-specific body (cash numpad, combined split, or a
 * card prompt). The footer confirms the sale once the method's input is
 * valid; Cancel / the close button always dismiss.
 *
 * Keyboard-driven (bos-seller#58): `PaymentScope` suspends the sale screen's
 * own shortcuts while open (`F1` stays live, like every other overlay).
 * `usePaymentPhysicalKeys` feeds `0`-`9`/`.`/`,`/`Backspace` into the same
 * numpad path from anywhere in the dialog and confirms on `Enter` unless a
 * button has focus. The method group takes initial focus
 * (`PaymentMethodSelect`); `←`/`→` switch it natively, and while the split is
 * selected `↑`/`↓` enter its legs instead (`useCombinedLegKeys`).
 */
export const PaymentModal: React.FC<PaymentFlowValue> = (flow) => {
    const { t } = useTranslation('pages');
    const {
        isModalOpen,
        closeModal,
        due,
        isDueKnown,
        subtotal,
        discountTotal,
        method,
        selectMethod,
        activeLeg,
        canConfirm,
        isSubmitting,
        confirm,
        onPadKey,
        onPadDelete,
        padDeleteDisabled,
    } = flow;

    const flashedKey = usePaymentPhysicalKeys({
        scope: PAYMENT_SCOPE,
        onKey: onPadKey,
        onDelete: onPadDelete,
        deleteDisabled: padDeleteDisabled,
        confirm,
    });
    const { legInputRefs, onMethodGroupKeyDown, onLegsKeyDown } =
        useCombinedLegKeys({ method, activeLeg });

    return (
        <>
            {isModalOpen ? <PaymentScope /> : null}
            <Modal.Backdrop
                isOpen={isModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeModal();
                    }
                }}
                variant="blur"
            >
                <Modal.Container placement="center" size="md">
                    <Modal.Dialog {...getNodeTestProps('seller-pay-modal')}>
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Icon>
                                <Icon
                                    className="size-5"
                                    icon="solar:card-bold-duotone"
                                />
                            </Modal.Icon>
                            <Modal.Heading>
                                {t('seller.payment.title')}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="flex flex-col gap-5">
                                <div className="rounded-2xl bg-surface-secondary p-4 text-center">
                                    <Typography
                                        className="uppercase tracking-wider"
                                        color="muted"
                                        type="body-xs"
                                    >
                                        {t('seller.payment.amountDue')}
                                    </Typography>
                                    <Typography
                                        className="mt-1 tabular-nums leading-none"
                                        {...getNodeTestProps('seller-pay-due')}
                                        type="h2"
                                        weight="bold"
                                    >
                                        {isDueKnown
                                            ? formatGel(due)
                                            : t('seller.discount.unknown')}
                                    </Typography>
                                    {isDueKnown && discountTotal > 0 ? (
                                        <div
                                            className="mt-2 flex items-center justify-center gap-2 tabular-nums"
                                            {...getNodeTestIdProp(
                                                'seller-pay-discounts',
                                            )}
                                        >
                                            <Typography
                                                className="line-through"
                                                color="muted"
                                                type="body-xs"
                                            >
                                                {formatGel(subtotal)}
                                            </Typography>
                                            {/* Foreground, not the warning-soft colour: on this surface that is 3.94:1. */}
                                            <Typography type="body-xs">
                                                {t('seller.payment.discounts', {
                                                    amount: formatGel(
                                                        discountTotal,
                                                    ),
                                                })}
                                            </Typography>
                                        </div>
                                    ) : null}
                                </div>

                                <PaymentMethodSelect
                                    isOpen={isModalOpen}
                                    method={method}
                                    onKeyDownCapture={onMethodGroupKeyDown}
                                    onSelect={selectMethod}
                                />

                                {method === 'cash' ? (
                                    <CashSection
                                        {...flow}
                                        flashedKey={flashedKey}
                                    />
                                ) : null}
                                {method === 'combined' ? (
                                    <CombinedSection
                                        {...flow}
                                        flashedKey={flashedKey}
                                        legInputRefs={legInputRefs}
                                        onLegsKeyDown={onLegsKeyDown}
                                    />
                                ) : null}
                                {method === 'card' ? (
                                    <TerminalSection {...flow} />
                                ) : null}
                            </div>
                        </Modal.Body>
                        {/* Wraps when Georgian labels don't fit one row (Cancel and Confirm need 471px of the 398px). */}
                        <Modal.Footer className="flex-wrap">
                            <Button
                                {...getNodeTestProps('seller-pay-cancel')}
                                onPress={closeModal}
                                render={renderCancelButton}
                                variant="outline"
                            >
                                {t('seller.payment.cancel')}
                                <ShortcutHint
                                    placement="control"
                                    shortcut={PAYMENT_CANCEL_SHORTCUT}
                                />
                            </Button>
                            <Button
                                {...getNodeTestProps('seller-pay-confirm')}
                                isDisabled={!canConfirm}
                                isPending={isSubmitting}
                                onPress={confirm}
                                render={renderConfirmButton}
                            >
                                {t('seller.payment.confirm')}
                                <ShortcutHint
                                    placement="control"
                                    shortcut={PAYMENT_CONFIRM_SHORTCUT}
                                />
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </>
    );
};
